/**
 * /login/verify?token=... — exchange magic-link token → session cookie.
 *
 * Spec: docs/specs/PORTAL_AUTHZ_AUTHN_SSOT.md, psm#68.
 *
 * Scanner-safe verification (PR-B4): the token is consumed ONLY by an explicit
 * user action (the "Sign in" button), never on passive page load. Email
 * security scanners / mailbox link-prefetch (Gmail, corporate gateways) issue a
 * GET — and may even execute the page JS — but they do not click the button, so
 * the magic-link token is never spent by a scanner. Previously this page
 * auto-POSTed the token on mount, so a scanner could consume the token first and
 * the real user then saw an "invalid link" error even though their account had
 * already advanced. See docs issue "magic-link scanner-safe verification".
 *
 * Already-used UX without weakening the API's UNIFORM invalid-token contract:
 * the BFF deliberately answers every deny reason (used / expired / cancelled /
 * unknown) with an identical `401 { ok:false, error:'INVALID_TOKEN' }` so the
 * response leaks nothing about token state (anti-enumeration — see
 * api/test/integration/portalAuth.test.ts "uniform invalid-token contract").
 * So instead of reading a token-state code, this page probes the SESSION
 * (`GET /api/portal/me`). A user who already completed verification holds a
 * session, so a repeat / double-click is surfaced as a friendly "already signed
 * in" instead of a hard error — and that safe UX is gated on the caller proving
 * they hold the session, never on the server disclosing token state.
 *
 * Critical: scrub the `token` query parameter from the URL via
 * history.replaceState immediately after capture so it does not leak into the
 * browser history, the Referer header on subsequent navigation, or the user's
 * bookmark / share buttons. The token is NEVER rendered in the DOM or logged.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PortalApiError } from '../lib/apiClient';
import { seedCsrfAndProbeSession, verifyLoginLink } from '../lib/auth';
import { sanitiseRedirect } from '../lib/redirect';

type VerifyState =
  | { kind: 'initialising' }
  | { kind: 'checking' }
  | { kind: 'ready' }
  | { kind: 'verifying' }
  | { kind: 'success'; email: string }
  | { kind: 'already' }
  | { kind: 'error'; message: string };

const MISSING_TOKEN_MESSAGE =
  'This sign-in link is not valid. Please open the link from your email again.';
const INVALID_TOKEN_MESSAGE =
  'This sign-in link is invalid or has expired. Please request a new sign-in link.';
const RATE_LIMITED_MESSAGE = 'Too many requests. Please wait a moment and try again.';
const NETWORK_ERROR_MESSAGE = 'A connection error occurred. Please try again.';
const REDIRECT_AFTER_MS = 1200;

export function LoginVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<VerifyState>({ kind: 'initialising' });
  // Hold the token in a ref so it never enters component state, never lands
  // in React DevTools props, never goes into screenshot-friendly DOM nodes.
  const tokenRef = useRef<string | null>(null);
  // Redirect to a safe relative target after success / already-signed-in.
  const redirectTo = useCallback(
    (target: string) => {
      window.setTimeout(() => {
        navigate(target, { replace: true });
      }, REDIRECT_AFTER_MS);
    },
    [navigate],
  );

  useEffect(() => {
    const rawToken = params.get('token');
    tokenRef.current = rawToken;

    // Strip ?token=... from the address bar BEFORE any await so the token
    // does not survive a Referer leak or hashchange navigation.
    if (rawToken && typeof window !== 'undefined' && window.history?.replaceState) {
      try {
        const scrubbed = new URL(window.location.href);
        scrubbed.searchParams.delete('token');
        window.history.replaceState(window.history.state, '', scrubbed.toString());
      } catch {
        // ignore; URL parsing should never fail in practice.
      }
    }

    if (!rawToken || rawToken.length < 16) {
      setState({ kind: 'error', message: MISSING_TOKEN_MESSAGE });
      return;
    }

    const ctrl = new AbortController();
    void (async () => {
      setState({ kind: 'checking' });
      // Seed portal_csrf for the (later) unsafe POST AND detect whether the
      // user is already signed in. This is a safe GET — it never consumes the
      // magic-link token, so a scanner that loads the page spends nothing.
      const outcome = await seedCsrfAndProbeSession(ctrl.signal);
      if (ctrl.signal.aborted) return;
      if (outcome === 'authenticated') {
        // Already verified in this browser — do not spend the token; just
        // continue into the app.
        tokenRef.current = null;
        setState({ kind: 'already' });
        redirectTo('/');
        return;
      }
      // Unauthenticated (or a transient probe error): show the explicit
      // sign-in action. The token is consumed only when the user clicks.
      setState({ kind: 'ready' });
    })();

    return () => ctrl.abort();
  }, [params, redirectTo]);

  const onVerify = useCallback(async () => {
    const rawToken = tokenRef.current;
    if (!rawToken) {
      setState({ kind: 'error', message: MISSING_TOKEN_MESSAGE });
      return;
    }
    const ctrl = new AbortController();
    setState({ kind: 'verifying' });
    try {
      const data = await verifyLoginLink(rawToken, ctrl.signal);
      tokenRef.current = null;
      // ログイン前に見ていた画面 (server が token に bind して返す) へ復帰する。
      // 二重に sanitise して open redirect を防ぎ、無ければ '/' へ。
      const target = sanitiseRedirect(data.redirect ?? null);
      setState({ kind: 'success', email: data.email });
      redirectTo(target);
    } catch (err) {
      if (err instanceof PortalApiError) {
        if (err.status === 429) {
          setState({ kind: 'error', message: RATE_LIMITED_MESSAGE });
          return;
        }
        if (err.status === 401 || err.status === 400) {
          // The API answers used / expired / unknown tokens with an identical
          // 401 (uniform contract). Distinguish "already verified" SAFELY by
          // probing the session: a caller who already completed verification
          // holds one. Never infer token state from the API response.
          const outcome = await seedCsrfAndProbeSession(ctrl.signal);
          tokenRef.current = null;
          if (outcome === 'authenticated') {
            setState({ kind: 'already' });
            redirectTo('/');
            return;
          }
          setState({ kind: 'error', message: INVALID_TOKEN_MESSAGE });
          return;
        }
      }
      tokenRef.current = null;
      setState({ kind: 'error', message: NETWORK_ERROR_MESSAGE });
    }
  }, [redirectTo]);

  return (
    <section style={{ maxWidth: '28rem' }}>
      <h1>Sign-in verification</h1>
      {state.kind === 'initialising' || state.kind === 'checking' || state.kind === 'verifying' ? (
        <p data-testid="verify-pending" aria-busy="true">
          {state.kind === 'verifying' ? 'Signing in…' : 'Loading…'}
        </p>
      ) : null}
      {state.kind === 'ready' ? (
        <>
          <p>Click below to finish signing in to your account.</p>
          <button type="button" data-testid="verify-button" onClick={onVerify}>
            Sign in
          </button>
        </>
      ) : null}
      {state.kind === 'success' ? (
        <p role="status" data-testid="verify-success">
          Signed in as {state.email}. Redirecting…
        </p>
      ) : null}
      {state.kind === 'already' ? (
        <p role="status" data-testid="verify-already">
          You are already signed in. Continuing… <Link to="/">Continue</Link>
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <>
          <div role="alert" data-testid="verify-error" style={{ color: 'crimson' }}>
            {state.message}
          </div>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/login">Request a new sign-in link</Link>
          </p>
        </>
      ) : null}
    </section>
  );
}
